import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PRIZES, PRIZE_CATEGORIES } from './data';

const formatPoints = (value: number) => `${value.toLocaleString('pt-BR')} pts`;

export default function PrizesCatalog() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('Todos');
  const scrollerRef = useRef<HTMLDivElement>(null);

  const filteredPrizes = useMemo(() => {
    if (activeCategory === 'Todos') return PRIZES;
    return PRIZES.filter((prize) => prize.category === activeCategory);
  }, [activeCategory]);

  const scrollByAmount = (direction: number) => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * node.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <section id="premios" className="scroll-mt-28 bg-secondary-50 py-20 md:py-28">
      <div className="mx-auto w-full max-w-[1240px] px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary-700">
            <i className="ri-gift-line"></i>
            Catálogo de prêmios
          </span>
          <h2 className="mt-5 font-heading text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-foreground-950 md:text-5xl">
            Um marketplace de prêmios <span className="text-primary-600">que todo mundo quer resgatar</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-foreground-600">
            Do PIX na hora às experiências inesquecíveis: o participante troca pontos por aquilo que realmente o motiva, em poucos cliques.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
          {PRIZE_CATEGORIES.map((category) => {
            const isActive = activeCategory === category;
            return (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  isActive
                    ? 'bg-primary-500 text-white'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:bg-primary-50'
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mx-auto mt-10 w-full max-w-[1240px] px-6">
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => scrollByAmount(-1)}
            aria-label="Prêmios anteriores"
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-800 transition-colors hover:bg-primary-50"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <button
            type="button"
            onClick={() => scrollByAmount(1)}
            aria-label="Próximos prêmios"
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-800 transition-colors hover:bg-primary-50"
          >
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        <div
          ref={scrollerRef}
          className="lp-scroll-hide mt-6 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2"
        >
          {filteredPrizes.map((prize) => (
            <button
              key={prize.id}
              type="button"
              onClick={() => navigate('/solicitar-demo')}
              className="group relative w-[82vw] max-w-[340px] shrink-0 snap-start cursor-pointer overflow-hidden rounded-3xl border border-background-200 bg-background-50 text-left transition-colors hover:border-primary-300 sm:w-[340px]"
            >
              <div className="relative aspect-square w-full overflow-hidden">
                <img
                  src={prize.image}
                  alt={prize.name}
                  className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                {prize.featured && (
                  <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-3 py-1 text-[11px] font-bold text-white">
                    Mais resgatado
                  </span>
                )}
                <span className="absolute right-4 top-4 rounded-full bg-background-50/90 px-3 py-1 text-[11px] font-semibold text-foreground-800 backdrop-blur-sm">
                  {prize.category}
                </span>
              </div>

              <div className="flex items-center justify-between gap-3 p-5">
                <div className="min-w-0">
                  <h3 className="truncate font-heading text-base font-bold text-foreground-950">{prize.name}</h3>
                  <p className="mt-1 text-sm font-semibold text-primary-600">{formatPoints(prize.points)}</p>
                </div>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700 transition-colors group-hover:bg-primary-500 group-hover:text-white">
                  <i className="ri-arrow-right-up-line text-lg"></i>
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-12 w-full max-w-[1240px] px-6">
        <div className="overflow-hidden rounded-[2rem] bg-primary-600">
          <div className="grid items-center gap-6 p-8 md:grid-cols-2 md:p-12">
            <div>
              <h3 className="font-heading text-2xl font-bold leading-tight text-background-50 md:text-3xl">
                Quer um catálogo sob medida para o seu time?
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-background-100 md:text-base">
                Montamos o portfólio de prêmios ideal para o perfil dos seus participantes, com a curadoria certa pra gerar desejo e engajamento.
              </p>
            </div>
            <div className="flex md:justify-end">
              <button
                type="button"
                onClick={() => navigate('/solicitar-demo')}
                className="inline-flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-7 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
              >
                Falar com especialista
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}